import { LANGUAGES } from '@app/core/constants/language.constants';

export const COURSE_INTENTS = {
  KNOWLEDGE_TRANSFER: 'knowledge_transfer',
  COMPLIANCE_AWARENESS: 'compliance_awareness',
  SKILLS_BUILDING: 'skills_building',
  ONBOARDING: 'onboarding',
  CERTIFICATION_PREP: 'certification_prep',
  AI_DECIDE: 'ai_decide',
} as const;

export type CourseIntent = (typeof COURSE_INTENTS)[keyof typeof COURSE_INTENTS];

export const TARGET_AUDIENCES = {
  NEW_TO_TOPIC: 'new_to_topic',
  SOME_EXPERIENCE: 'some_experience',
  EXPERTS: 'experts',
} as const;

export type TargetAudience =
  (typeof TARGET_AUDIENCES)[keyof typeof TARGET_AUDIENCES];

export interface OptionConfig<T extends string = string> {
  readonly id: T;
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly icon: string;
}

export const COURSE_INTENT_OPTIONS: readonly OptionConfig<CourseIntent>[] = [
  {
    id: COURSE_INTENTS.KNOWLEDGE_TRANSFER,
    titleKey: 'guidedCourseCreation.intent.knowledgeTransfer.title',
    descriptionKey: 'guidedCourseCreation.intent.knowledgeTransfer.description',
    icon: 'auto_stories',
  },
  {
    id: COURSE_INTENTS.COMPLIANCE_AWARENESS,
    titleKey: 'guidedCourseCreation.intent.complianceAwareness.title',
    descriptionKey:
      'guidedCourseCreation.intent.complianceAwareness.description',
    icon: 'shield',
  },
  {
    id: COURSE_INTENTS.SKILLS_BUILDING,
    titleKey: 'guidedCourseCreation.intent.skillsBuilding.title',
    descriptionKey: 'guidedCourseCreation.intent.skillsBuilding.description',
    icon: 'build',
  },
  {
    id: COURSE_INTENTS.ONBOARDING,
    titleKey: 'guidedCourseCreation.intent.onboarding.title',
    descriptionKey: 'guidedCourseCreation.intent.onboarding.description',
    icon: 'person_add',
  },
  {
    id: COURSE_INTENTS.CERTIFICATION_PREP,
    titleKey: 'guidedCourseCreation.intent.certificationPrep.title',
    descriptionKey: 'guidedCourseCreation.intent.certificationPrep.description',
    icon: 'workspace_premium',
  },
  {
    id: COURSE_INTENTS.AI_DECIDE,
    titleKey: 'guidedCourseCreation.intent.aiDecide.title',
    descriptionKey: 'guidedCourseCreation.intent.aiDecide.description',
    icon: 'smart_toy',
  },
];

export const TARGET_AUDIENCE_OPTIONS: readonly OptionConfig<TargetAudience>[] =
  [
    {
      id: TARGET_AUDIENCES.NEW_TO_TOPIC,
      titleKey: 'guidedCourseCreation.audience.newToTopic.title',
      descriptionKey: 'guidedCourseCreation.audience.newToTopic.description',
      icon: 'face',
    },
    {
      id: TARGET_AUDIENCES.SOME_EXPERIENCE,
      titleKey: 'guidedCourseCreation.audience.someExperience.title',
      descriptionKey:
        'guidedCourseCreation.audience.someExperience.description',
      icon: 'hail',
    },
    {
      id: TARGET_AUDIENCES.EXPERTS,
      titleKey: 'guidedCourseCreation.audience.experts.title',
      descriptionKey: 'guidedCourseCreation.audience.experts.description',
      icon: 'work',
    },
  ];

export interface GuidedCourseCreationFormValue {
  topic: string;
  language: LANGUAGES;
  intent: CourseIntent;
  audience: TargetAudience[];
}

export interface CourseModuleOutline {
  name: string;
  moduleType: string;
  estimatedTimeMinutes: number;
  whatToCover: string;
  iconPath?: string;
}

export interface CourseSectionOutline {
  headline: string;
  modules: CourseModuleOutline[];
}

export interface CourseOutline {
  title: string;
  description: string;
  totalTimeMinutes: number;
  sections: CourseSectionOutline[];
}

export interface BulkCreateParagraph {
  paragraph_number: number;
  suggested_module: string;
  module_headline: string;
  what_to_cover: string;
  estimated_duration_minutes: number;
}

export interface BulkCreateSection {
  section_number: number;
  paragraphs: BulkCreateParagraph[];
}

export interface BulkCreatePayload {
  language: string;
  sections: BulkCreateSection[];
}

export interface BulkCreateModuleResult {
  content_id: string;
  h5p_library: string;
  content_json: string;
  paragraph_number: number;
  estimated_duration_minutes: number;
}

export interface BulkCreateSectionResult {
  title: string;
  modules: BulkCreateModuleResult[];
}

export interface BulkCreateResponse {
  sections: BulkCreateSectionResult[];
}

export function buildBulkCreatePayload(
  outline: CourseOutline,
  language: string,
): BulkCreatePayload {
  return {
    language,
    sections: outline.sections.map((section, sectionIndex) => ({
      section_number: sectionIndex + 1,
      paragraphs: section.modules.map((mod, modIndex) => ({
        paragraph_number: modIndex + 1,
        suggested_module: mod.moduleType,
        module_headline: mod.name,
        what_to_cover: mod.whatToCover,
        estimated_duration_minutes: mod.estimatedTimeMinutes,
      })),
    })),
  };
}

export const MOCK_COURSE_OUTLINE: CourseOutline = {
  title: 'H5P Module Types — Full Coverage Test',
  description:
    'A test course exercising every supported H5P module type through bulk creation.',
  totalTimeMinutes: 220,
  sections: [
    {
      headline: 'Content Activities',

      modules: [
        {
          name: 'Content Page Module',
          moduleType: 'Content',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test content page module creation.',
        },
        {
          name: 'Interactive Video Module',
          moduleType: 'Interactive Video',
          estimatedTimeMinutes: 15,
          whatToCover: 'Test interactive video module creation.',
        },
        {
          name: 'Audio Module',
          moduleType: 'Audio',
          estimatedTimeMinutes: 5,
          whatToCover: 'Test audio module creation.',
        },
        {
          name: 'Checklist Module',
          moduleType: 'Checklist',
          estimatedTimeMinutes: 5,
          whatToCover: 'Test checklist module creation.',
        },
        {
          name: 'Slides Module',
          moduleType: 'Slides',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test slides module creation.',
        },
      ],
    },
    {
      headline: 'Media & Links',

      modules: [
        {
          name: 'Hotspots Module',
          moduleType: 'Hotspots',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test image hotspots module creation.',
        },
        {
          name: 'Feedback Module',
          moduleType: 'Feedback',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test feedback/questionnaire module creation.',
        },
        {
          name: 'Study Cards Module',
          moduleType: 'Study Cards',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test dialog cards module creation.',
        },
        {
          name: 'Link Module',
          moduleType: 'Link',
          estimatedTimeMinutes: 5,
          whatToCover: 'Test link/iframe embedder module creation.',
        },
        {
          name: 'Image Slider Module',
          moduleType: 'Image Slider',
          estimatedTimeMinutes: 5,
          whatToCover: 'Test image slider module creation.',
        },
      ],
    },
    {
      headline: 'Quiz Activities',

      modules: [
        {
          name: 'Quiz Module',
          moduleType: 'Quiz',
          estimatedTimeMinutes: 15,
          whatToCover: 'Test quiz/question set module creation.',
        },
        {
          name: 'Drag the Words Module',
          moduleType: 'Drag the Words',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test drag the words module creation.',
        },
        {
          name: 'Memory Game Module',
          moduleType: 'Memory Game',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test memory game module creation.',
        },
        {
          name: 'Hotspot Search Module',
          moduleType: 'Hotspot search',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test image hotspot question module creation.',
        },
        {
          name: 'Drag and Drop Module',
          moduleType: 'Drag and Drop',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test drag and drop module creation.',
        },
      ],
    },
    {
      headline: 'Advanced & Integrations',

      modules: [
        {
          name: 'SCORM Module',
          moduleType: 'SCORM',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test SCORM module creation.',
        },
        {
          name: 'LTI Module',
          moduleType: 'LTI',
          estimatedTimeMinutes: 5,
          whatToCover: 'Test LTI module creation.',
        },
        {
          name: 'Task Module',
          moduleType: 'Task',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test task module creation.',
        },
        {
          name: 'Sort the Paragraphs Module',
          moduleType: 'Sort the Paragraphs',
          estimatedTimeMinutes: 10,
          whatToCover: 'Test sort the paragraphs module creation.',
        },
      ],
    },
  ],
};
